import { useState } from 'react';

export interface HelpStep {
  icon: string;
  title: string;
  description: string;
  tips?: string[];
}

interface HelpSection {
  label: string;
  steps: HelpStep[];
}

interface Props {
  title: string;
  sections: HelpSection[];
  isOpen: boolean;
  onClose: () => void;
}

export default function HelpModal({ title, sections, isOpen, onClose }: Props) {
  const [activeSection, setActiveSection] = useState(0);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-xl rounded-2xl overflow-hidden"
        style={{ background: '#fff', border: '1px solid #e8d8cc', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid #e8d8cc', background: 'linear-gradient(135deg, #fff8f2 0%, #fdf2e8 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-9 h-9 rounded-full"
              style={{ background: '#c4903a' }}
            >
              <i className="ri-question-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#c4903a' }}>使い方ガイド</p>
              <h2 className="font-bold text-base" style={{ color: '#4a3728' }}>{title}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full cursor-pointer transition-colors hover:opacity-70"
            style={{ color: '#9b8b7f' }}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tab Nav (if multiple sections) */}
        {sections.length > 1 && (
          <div
            className="flex gap-1 px-4 pt-3 pb-0 flex-shrink-0"
            style={{ borderBottom: '1px solid #e8d8cc', background: '#fff' }}
          >
            {sections.map((sec, i) => (
              <button
                key={i}
                onClick={() => setActiveSection(i)}
                className="px-4 py-2 text-sm font-bold rounded-t-lg transition-all cursor-pointer whitespace-nowrap"
                style={{
                  background: activeSection === i ? '#fff8f2' : 'transparent',
                  color: activeSection === i ? '#c4903a' : '#9b8b7f',
                  borderBottom: activeSection === i ? '2px solid #c4903a' : '2px solid transparent',
                }}
              >
                {sec.label}
              </button>
            ))}
          </div>
        )}

        {/* Steps */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          <div className="flex flex-col gap-4">
            {sections[activeSection].steps.map((step, i) => (
              <div key={i} className="flex gap-4">
                {/* Step Number + Line */}
                <div className="flex flex-col items-center flex-shrink-0">
                  <div
                    className="flex items-center justify-center w-9 h-9 rounded-full font-bold text-sm flex-shrink-0"
                    style={{ background: '#fff0e0', color: '#c4903a', border: '2px solid #f0d8a0' }}
                  >
                    {i + 1}
                  </div>
                  {i < sections[activeSection].steps.length - 1 && (
                    <div className="w-px flex-1 mt-1" style={{ background: '#e8d8cc', minHeight: '20px' }}></div>
                  )}
                </div>

                {/* Content */}
                <div className="pb-4 flex-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="flex items-center justify-center w-5 h-5">
                      <i className={`${step.icon} text-base`} style={{ color: '#c4903a' }}></i>
                    </span>
                    <h3 className="font-bold text-sm" style={{ color: '#4a3728' }}>{step.title}</h3>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#6b5a4e' }}>
                    {step.description}
                  </p>
                  {step.tips && step.tips.length > 0 && (
                    <div
                      className="mt-2 px-3 py-2 rounded-lg"
                      style={{ background: '#fff8f2', border: '1px solid #f0d8a0' }}
                    >
                      {step.tips.map((tip, j) => (
                        <div key={j} className="flex items-start gap-2 text-xs" style={{ color: '#9b6b2a' }}>
                          <span className="flex items-center justify-center w-3 h-3 mt-0.5 flex-shrink-0">
                            <i className="ri-lightbulb-line text-xs"></i>
                          </span>
                          <span>{tip}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between px-6 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid #e8d8cc', background: '#fff8f2' }}
        >
          <p className="text-xs" style={{ color: '#9b8b7f' }}>
            <span className="flex items-center justify-center w-3 h-3 inline-flex">
              <i className="ri-information-line text-xs"></i>
            </span>
            {' '}ご不明な点はスタッフまでお気軽にご連絡ください
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-bold cursor-pointer whitespace-nowrap transition-opacity hover:opacity-80"
            style={{ background: '#c4903a', color: '#fff' }}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}